<!-- @format -->

<template>
  <!-- Keyboard/screen-reader shortcut past the header nav; hidden until focused
       (Bootstrap visually-hidden-focusable) — targets the <main> landmark. -->
  <a href="#main-content" class="skip-link visually-hidden-focusable"
    >Skip to main content</a
  >
  <div class="sale-promo-banner">
    <strong
      >Free Ground Shipping on orders {{ freeShippingThresholdText() }}+
      🚚</strong
    >
  </div>
  <!-- Experiment-driven announcement bar (promo_banner surface, bead zpe.2).
       Server-authored payload; renders nothing unless a variant with text
       resolves, so today's default state is unchanged. -->
  <PromoBanner />
  <div class="container">
    <header>
      <nav class="row text-right" aria-label="Site">
        <div class="col">
          <RouterLink
            class="mx-2 home icon-buttons"
            :to="{ name: 'home' }"
            aria-label="Home"
          >
            <IconComponent name="house" aria-hidden="true"> </IconComponent>
          </RouterLink>
          <RouterLink
            aria-label="Frequently asked questions"
            class="mx-2 support icon-buttons"
            :to="{ name: 'faqs', params: {} }"
          >
            <IconComponent name="question-circle" aria-hidden="true">
            </IconComponent>
          </RouterLink>
          <RouterLink
            aria-label="Favorites"
            class="mx-2 favorites icon-buttons"
            :to="{ name: 'favorites', params: {} }"
          >
            <IconComponent name="heart" aria-hidden="true"></IconComponent>
          </RouterLink>
          <RouterLink
            :aria-label="
              cartLength
                ? `Shopping cart, ${cartLength} item${cartLength === 1 ? '' : 's'}`
                : 'Shopping cart'
            "
            class="mx-2 cart icon-buttons"
            :to="{ name: 'cart', params: {} }"
          >
            <IconComponent name="cart3" aria-hidden="true"></IconComponent
            ><span v-if="cartLength" style="text-decoration: none"
              ><sup>{{ cartLength }}</sup></span
            >
          </RouterLink>
        </div>
      </nav>
      <div class="row">
        <div class="col">
          <div class="text-right">
            <nav class="pt-2" aria-label="Collections">
              <RouterLink
                class="rainbow-link"
                :to="{
                  name: 'collection',
                  params: { collectionSlug: 'whats-new' },
                }"
                >What's New 🎉
              </RouterLink>
              <RouterLink
                class="rainbow-link"
                :to="{
                  name: 'collection',
                  params: { collectionSlug: 'california' },
                }"
                >California
              </RouterLink>
              <RouterLink
                class="rainbow-link"
                :to="{
                  name: 'collection',
                  params: { collectionSlug: 'wine-country' },
                }"
                >Wine Country
              </RouterLink>
              <RouterLink
                class="rainbow-link"
                :to="{
                  name: 'collection',
                  params: { collectionSlug: 'landscapes' },
                }"
                >Landscapes
              </RouterLink>
              <br />
              <RouterLink
                class="rainbow-link"
                :to="{
                  name: 'collection',
                  params: { collectionSlug: 'cities-and-urbanscapes' },
                }"
                >Cities
              </RouterLink>
              <RouterLink
                class="rainbow-link"
                :to="{
                  name: 'collection',
                  params: { collectionSlug: 'black-and-white' },
                }"
                >B&W
              </RouterLink>
              <RouterLink
                class="rainbow-link"
                :to="{
                  name: 'collection',
                  params: { collectionSlug: 'destinations' },
                }"
                >Travel
              </RouterLink>
              <RouterLink
                class="rainbow-link"
                :to="{
                  name: 'collection',
                  params: { collectionSlug: 'flora-and-fauna' },
                }"
                >Nature
              </RouterLink>
            </nav>
            <RouterLink class="logo rainbow" :to="{ name: 'home' }"
              >Outstanding Photos</RouterLink
            >
            <hr class="logo-divider" />
          </div>
        </div>
      </div>
    </header>
    <main id="main-content" tabindex="-1">
      <slot></slot>
    </main>
    <footer>
      <div class="row">
        <div class="col footer">
          <ul class="footer-links list-inline">
            <li class="list-inline-item">
              <RouterLink
                alt="About"
                title="About"
                class="footer-link"
                :to="{ name: 'about', params: {} }"
                >About</RouterLink
              >
            </li>
            <li class="list-inline-item">
              <RouterLink
                alt="Stories"
                title="Stories"
                class="footer-link"
                :to="{ name: 'stories', params: {} }"
                >Stories</RouterLink
              >
            </li>
            <li class="list-inline-item">
              <RouterLink
                alt="Frequently Asked Questions"
                title="FAQs"
                class="footer-link"
                :to="{ name: 'faqs', params: {} }"
                >FAQs</RouterLink
              >
            </li>
            <li class="list-inline-item">
              <RouterLink
                alt="Support"
                title="Support"
                class="footer-link"
                :to="{ name: 'support', params: {} }"
                >Support</RouterLink
              >
            </li>

            <li class="list-inline-item">
              <RouterLink
                alt="Refund Policy"
                title="Refund Policy"
                class="footer-link"
                :to="{ name: 'refunds', params: {} }"
                >Refund Policy</RouterLink
              >
            </li>
            <li class="list-inline-item">
              <RouterLink
                alt="Privacy Policy"
                title="Privacy Policy"
                class="footer-link"
                :to="{ name: 'privacy', params: {} }"
                >Privacy</RouterLink
              >
            </li>
            <li class="list-inline-item">
              <RouterLink
                alt="Terms of Service"
                title="Terms of Service"
                class="footer-link"
                :to="{ name: 'terms', params: {} }"
                >Terms</RouterLink
              >
            </li>
          </ul>
        </div>
      </div>
      <div class="row">
        <div class="col footer">
          <form class="footer-signup" @submit.prevent="submitSignup" novalidate>
            <label for="footer-email-signup" class="footer-signup-label">
              Field notes from the darkroom — and 10% off your first print.
            </label>
            <div class="footer-signup-row">
              <input
                id="footer-email-signup"
                v-model="signupEmail"
                type="email"
                class="form-control footer-signup-input"
                placeholder="you@example.com"
                autocomplete="email"
                aria-label="Email address"
                :disabled="signupState === 'submitting' || signupState === 'done'"
                required
              />
              <button
                type="submit"
                class="btn btn-dark footer-signup-btn"
                :disabled="signupState === 'submitting' || signupState === 'done'"
              >
                {{ signupState === "submitting" ? "Sending…" : "Subscribe" }}
              </button>
            </div>
            <p
              v-if="signupMessage"
              class="footer-signup-msg small"
              :class="signupState === 'error' ? 'text-danger' : 'text-muted'"
              role="status"
              aria-live="polite"
            >
              {{ signupMessage }}
            </p>
          </form>
        </div>
      </div>
      <div class="row">
        <div class="col footer">
          <span class="text-muted footer-copyright"
            >All images and text &copy; Outstanding Photos 2026</span
          ><span class="text-muted footer-copyright" style="display: block"
            >Made with 🍷 in Napa, CA</span
          >
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
/** @format */

import { computed, ref } from "vue";
import axios from "axios";
import PromoBanner from "@/components/PromoBanner.vue";
import { useCart } from "@/composables/useCart";
import logger from "@/utils/logger";
// Free-shipping threshold from the ONE SPA shipping-facts source (bead 49n.3b)
// so the promo bar can't hard-code a value the server has changed.
import { freeShippingThresholdText } from "@/content/shippingFacts";

// Header cart badge reads the shared cart store directly — no prop-drilling.
const { cart } = useCart();
const cartLength = computed(() => cart.items.length || 0);

// Footer email-capture → first-order discount (bead 2rk). The API validates
// authoritatively and is idempotent on the email; this does a light client check
// and shows a quiet confirmation. Fire-and-forget-ish: a failure never navigates.
type SignupState = "idle" | "submitting" | "done" | "error";
const signupEmail = ref("");
const signupState = ref<SignupState>("idle");
const signupMessage = ref("");

async function submitSignup(): Promise<void> {
  const email = signupEmail.value.trim();
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
    signupState.value = "error";
    signupMessage.value = "Please enter a valid email address.";
    return;
  }
  signupState.value = "submitting";
  signupMessage.value = "";
  try {
    await axios.post(
      "/api/subscribe",
      { email, source: "footer" },
      { skipGlobalErrorRedirect: true },
    );
    signupState.value = "done";
    signupMessage.value =
      "Thanks — your code is on its way. Check your inbox (and your spam folder).";
  } catch (error) {
    signupState.value = "error";
    signupMessage.value = "Something went wrong. Please try again.";
    logger.debug("Footer signup failed", { error: (error as Error).message });
  }
}
</script>

<style scoped>
/** @format */

a.logo {
  font-weight: 300;
  letter-spacing: 3px;
  display: inline-block;
  margin-bottom: 0;
  position: relative;
  top: 7px;
  font-size: 2rem;
}

a {
  text-decoration-line: none !important;
}

a.rainbow-link {
  color: rgb(63, 63, 63) !important;
  font-size: 0.8rem;
  padding: 0 8px;
  font-weight: normal;
  text-transform: uppercase;
}

a.rainbow-link:hover {
  color: rgb(0, 0, 0) !important;
  text-decoration-line: underline !important;
}

/* Skip link: hidden until keyboard-focused (Bootstrap visually-hidden-focusable
   handles the hide/reveal), then pinned top-left as a visible target. */
.skip-link:focus {
  position: fixed;
  top: 0.5rem;
  left: 0.5rem;
  z-index: 3000;
  padding: 0.5rem 0.75rem;
  background: #fff;
  color: #333;
  border: 1px solid #333;
  border-radius: 4px;
}

/* The <main> landmark is a programmatic focus target for the skip link; no
   visible ring needed around the whole content region. */
main:focus {
  outline: none;
}

/* Footer email-capture (bead 2rk) — quiet, centered, on-brand. */
.footer-signup {
  max-width: 420px;
  margin: 0.5rem auto 1rem;
  text-align: center;
}

/* The one line meant to draw the eye in the footer — sits a step above the
   fine-print tier (.footer nav links at 0.8rem). */
.footer-signup-label {
  display: block;
  font-size: 0.95rem;
  color: #5a5a5a;
  margin-bottom: 0.5rem;
}

.footer-signup-row {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
}

.footer-signup-input {
  flex: 1 1 auto;
  max-width: 260px;
}

.footer-signup-btn {
  flex: 0 0 auto;
}

.footer-signup-msg {
  margin-top: 0.5rem;
  margin-bottom: 0;
}

/* Bottom of the footer scale: the quietest, smallest line. */
.footer-copyright {
  font-size: 0.72rem;
  line-height: 1.6;
}

/* Styling for the top banner */
.sale-promo-banner {
  background-color: #fdecd5;
  /* a soft peach color */
  color: #5a5a5a;
  /* muted dark color for the text */
  text-align: center;
  /* center the text */
  padding: 8px 0;
  /* subtle padding top and bottom */
  font-size: 1rem;
  /* standard text size */
  letter-spacing: 0.5px;
  /* minimal letter spacing */
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  /* a very soft shadow for a touch of depth */
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  /* a subtle top border */
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  /* a subtle bottom border */
}
</style>
